import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { myExpariencesService } from "../../../services";
import { useSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LineChart from "../../../components/charts/LineChart";
import {
  createLegalAgeDate,
  formatForRequest,
  guidGenerator,
  useViewport,
} from "../../../helpers";
import GlobeGl from "../../../components/globe/GlobeGl";
import { useParams } from "react-router-dom";
import {
  Add,
  Cancel,
  Clear,
  ExpandMore,
  FilterListOff,
  Sort,
} from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import i18next from "i18next";
import { OpenStreetMapProvider } from "leaflet-geosearch";

import Lottie from "lottie-react";
import ShareLottie from "../../../assets/shareLottie.json";
import RocketLaunch from "../../../assets/rocketLaunchLottie.json";
import ViewLottie from "../../../assets/viewLottie.json";
import LikeLottie from "../../../assets/likeLottie.json";
import WebviewLottie from "../../../assets/webviewLottie.json";
import PaymentLottie from "../../../assets/paymentLottie.json";
import PacesLottie from "../../../assets/paces.json";
import UniqueLottie from "../../../assets/unique.json";
import ClassStats from "../classes/ClassStats";

const PREFIX = "MyExparienceStats";

const classes = {
  root: `${PREFIX}-root`,
  loadingContainer: `${PREFIX}-loadingContainer`,
  card: `${PREFIX}-card`,
  textContainer: `${PREFIX}-textContainer`,
  iconStyle: `${PREFIX}-iconStyle`,
  seeMoreButton: `${PREFIX}-seeMoreButton`,
  seeMoreButtonText: `${PREFIX}-seeMoreButtonText`,
  switchButton: `${PREFIX}-switchButton`,
  dateAndLocation: `${PREFIX}-dateAndLocation`,
  firstAccordionStyle: `${PREFIX}-firstAccordionStyle`,
  firstAccordionStyleFromUserDashboard: `${PREFIX}-firstAccordionStyleFromUserDashboard`,
  textAndIconStyle: `${PREFIX}-textAndIconStyle`,
  customDatePickers: `${PREFIX}-customDatePickers`,
  selectLocationStyle: `${PREFIX}-selectLocationStyle`,
  autocomplete: `${PREFIX}-autocomplete`,
  defaultDiv: `${PREFIX}-defaultDiv`,
  locationsContainer: `${PREFIX}-locationsContainer`,
  mainButtonsContainer: `${PREFIX}-mainButtonsContainer`,
  sortLocationButton: `${PREFIX}-sortLocationButton`,
  frameOptionsContainer: `${PREFIX}-frameOptionsContainer`,
  optionsFormat: `${PREFIX}-optionsFormat`,
  frameTotalStyleQuantity: `${PREFIX}-frameTotalStyleQuantity`,
  deleteIconStyle: `${PREFIX}-deleteIconStyle`,
  radioGroupStyle: `${PREFIX}-radioGroupStyle`,
  cardsTitleMargin: `${PREFIX}-cardsTitleMargin`,
  closeIcon: `${PREFIX}-closeIcon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    marginTop: "1rem",
    maxWidth: "650px",
    display: "flex",
    flexWrap: "wrap",
    margin: "0 auto",
    [theme.breakpoints.between("xs", "md")]: {
      justifyContent: "space-evenly",
    },
  },

  [`& .${classes.loadingContainer}`]: {
    textAlign: "center",
    marginTop: "1rem",
  },

  [`& .${classes.card}`]: {
    backgroundColor: "#424242",
    width: "300px",
    position: "relative",
    height: "150px",
    "& > div": {
      padding: "5px 15px",
    },
    [theme.breakpoints.down("md")]: {
      width: "150px",
      height: "130px",
      margin: "0 0 1rem 0",
      "& > div": {
        padding: "8px",
      },
    },
  },

  [`& .${classes.textContainer}`]: {
    width: "47%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    padding: "0.5rem 0",
    [theme.breakpoints.between("xs", "md")]: {
      alignItems: "center",
      width: "100%",
      justifyContent: "flex-start",
      padding: "0",
    },
  },

  [`& .${classes.iconStyle}`]: {
    width: "80px",
    height: "80px",
    [theme.breakpoints.down("md")]: {
      width: "40px",
      height: "40px",
    },
  },

  [`& .${classes.seeMoreButton}`]: {
    position: "absolute",
    right: "15px",
    bottom: "14px",
    height: "35px",
    [theme.breakpoints.down("md")]: {
      right: "15%",
      bottom: "8px",
      transform: "translate(-15%)",
      height: "25px",
    },
  },

  [`& .${classes.seeMoreButtonText}`]: {
    color: theme.palette.primary.main,
    fontSize: "12px",
  },

  [`& .${classes.switchButton}`]: {
    position: "absolute",
    top: "15px",
    left: "9%",
  },

  [`& .${classes.dateAndLocation}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: "0 1rem",
  },

  [`& .${classes.firstAccordionStyle}`]: {
    maxWidth: "320px",
    marginBottom: "1rem",
    backgroundColor: "#323232 !important",
    [theme.breakpoints.up("md")]: {
      maxWidth: "620px",
    },
  },

  [`& .${classes.firstAccordionStyleFromUserDashboard}`]: {
    marginBottom: "1rem",
    backgroundColor: "#323232 !important",
  },

  [`& .${classes.textAndIconStyle}`]: {
    color: "#D3AF5F",
  },

  [`& .${classes.customDatePickers}`]: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
    margin: "1rem 0",
  },

  [`& .${classes.selectLocationStyle}`]: {
    "&.MuiFormControl-root": {
      margin: "1rem 0",
      width: "100%",
      maxWidth: "550px",
    },
  },

  [`& .${classes.autocomplete}`]: {
    width: "55%",
    maxWidth: "550px",
  },

  [`& .${classes.defaultDiv}`]: {
    display: "flex",
    justifyContent: "space-evenly",
    width: "100%",
    marginBottom: "1rem",
  },

  [`& .${classes.locationsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
  },

  [`& .${classes.mainButtonsContainer}`]: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.sortLocationButton}`]: {
    marginLeft: "2rem",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0",
      marginTop: "0.5rem",
    },
  },

  [`& .${classes.frameOptionsContainer}`]: {
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "15px",
    padding: "0.5rem",
  },

  [`& .${classes.optionsFormat}`]: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    padding: "0.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxHeight: "350px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.frameTotalStyleQuantity}`]: {
    color: "white !important",
    width: "28%",
    "& .MuiOutlinedInput-input": {
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
      },
    },
    "& .Mui-error": {
      color: "#FFFFFFB3",
    },
  },

  [`& .${classes.deleteIconStyle}`]: {
    "&.MuiIconButton-root": {
      padding: "0",
    },
  },

  [`& .${classes.radioGroupStyle}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      display: "flex",
      justifyContent: "space-between",
    },
  },

  [`& .${classes.cardsTitleMargin}`]: {
    marginBottom: "0.5rem",
  },
}));

const MyExparienceStats = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const { id } = useParams();

  const provider = new OpenStreetMapProvider();

  const [customLocation, setCustomLocation] = useState([]);
  const [debounce, setDebounce] = useState({});
  const [stats, setStats] = useState(null);
  const [unfilteredStats, setUnfilteredStats] = useState(null);
  const [statToGraph, setStatToGraph] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [detailMode, setDetailMode] = useState(null);
  const [globe, setGlobe] = useState(false);
  const [classesTab, setClassesTab] = useState({ open: false, data: null });
  const [filterIdValue, setFilterIdValue] = useState(null);
  const [location, setLocation] = useState([]);
  const [timeframe, setTimeframe] = useState({
    mode: "ever",
    startDate: null,
    endDate: null,
  });
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 250,
      },
    },
  };

  useEffect(() => {
    const { cb, delay } = debounce;
    if (cb) {
      const timeout = setTimeout(cb, delay);
      return () => clearTimeout(timeout);
    }
  }, [debounce]);

  useEffect(() => {
    if (timeframe.mode === "month")
      setTimeframe({
        ...timeframe,
        startDate: formatForRequest(createLegalAgeDate(0, -1, 0)),
        endDate: formatForRequest(new Date()),
      });
    else if (timeframe.mode === "day")
      setTimeframe({
        ...timeframe,
        startDate: formatForRequest(createLegalAgeDate(-7, 0, 0)),
        endDate: formatForRequest(new Date()),
      });
    else if (timeframe.mode === "yesterday")
      setTimeframe({
        ...timeframe,
        startDate: formatForRequest(createLegalAgeDate(-1, 0, 0)),
        endDate: formatForRequest(new Date()),
      });
    else setTimeframe({ ...timeframe, startDate: null, endDate: null });
  }, [timeframe.mode]);

  const handleEmptyCustom = () => {
    let isEmpty = false;

    customLocation.forEach((elem) => {
      if (!elem.label || elem.radius === "") isEmpty = true;
    });
    return isEmpty;
  };

  const generateStartAndEndDate = (startDate = true) => {
    if (timeframe.mode === "month")
      return startDate
        ? dayjs().startOf("month").format("YYYY-MM-DD")
        : dayjs().endOf("month").format("YYYY-MM-DD");
    else if (timeframe.mode === "day")
      return startDate
        ? dayjs().startOf("week").format("YYYY-MM-DD")
        : dayjs().endOf("week").format("YYYY-MM-DD");
    else if (timeframe.mode === "yesterday")
      return startDate ? dayjs().add(-1, "day").format("YYYY-MM-DD") : dayjs();
    else if (timeframe.mode === "custom")
      return startDate ? timeframe.startDate : timeframe.endDate;
    else return null;
  };

  const handleFilters = () => {
    let bodyToSend = {
      positions: customLocation.map((item) => ({
        latitude: item.coordinates[0],
        longitude: item.coordinates[1],
        distance: parseInt(item.radius),
      })),
      startDate: generateStartAndEndDate(true),
      endDate: generateStartAndEndDate(false),
    };
    if (bodyToSend.startDate === null) delete bodyToSend.startDate;
    if (bodyToSend.endDate === null) delete bodyToSend.endDate;
    bodyToSend.positions = [
      ...bodyToSend.positions,
      ...location.map((item) => ({
        latitude: item.position.coordinates[0],
        longitude: item.position.coordinates[1],
        distance: item.position.radius,
      })),
    ];
    myExpariencesService
      .filterStats(bodyToSend, props.exparience.id)
      .then((res) => {
        setStats(res);
        enqueueSnackbar(t("toasts.filterStatsSuccess"), { variant: "success" });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.filterStatsFailed"), { variant: "error" });
      });
  };

  const promiseOptions = (value, index) => {
    let tmp = [...customLocation];
    tmp[index] = { ...tmp[index], loading: true };
    setCustomLocation(tmp);
    setDebounce({
      cb: async () => {
        const tmpLocationResult = await provider.search({ query: value });
        const tmpCallback = tmpLocationResult.map((element) => ({
          label: element.label,
          value: element.label,
          coordinates: [element.x, element.y],
          place_id: element.raw.place_id,
        }));
        tmp = [...customLocation];
        tmp[index] = { ...tmp[index], loading: false, options: tmpCallback };
        setCustomLocation(tmp);
      },
      delay: 500, //ms
    });
  };

  useEffect(() => {
    if (!stats || id) {
      setReloading(true);
      myExpariencesService
        .fetchExparienceStats(id)
        .then((res) => {
          // res.payments = [{"_id":"627bdb3b2e552d0011140d5e","user":186,"exparienceId":"627527b5b976002109411031","store":"appStore","originalPurchaseDate":"2022-05-11T15:50:16.000Z","productIdentifier":"faz_099_24h","revenueCatId":"ads_186_mmonin_admin","__v":0,"id":"627bdb3b2e552d0011140d5e","remainingTime":25950608}]
          setStats(res);
          setUnfilteredStats(res);
          setReloading(false);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.fetchExparienceStatsError"), {
              variant: "error",
            });
        });
    }
  }, [id]);

  const CustomCard = ({
    title,
    value,
    caption,
    icon,
    id,
    disableClick = false,
  }) => {
    const agregDataByDate = (dataById, dateAccessKey = "createdDate") => {
      const tmp = {};
      dataById.forEach((data) => {
        const tmpDate = data[dateAccessKey].substr(0, 10);
        if (tmp.hasOwnProperty(tmpDate)) tmp[tmpDate] += 1;
        else tmp[tmpDate] = 1;
      });
      const ordered = Object.keys(tmp)
        .sort()
        .reduce((obj, key) => {
          obj[key] = tmp[key];
          return obj;
        }, {});
      const finalTmp = {
        labels: Object.keys(ordered),
        values: Object.values(ordered),
      };
      return finalTmp;
    };

    const handleStatsForGraph = (id) => {
      let tmp = {};
      if (id === "payments-new") {
        let paymentDates = agregDataByDate(
          stats["payments-new"].data,
          "originalPurchaseDate"
        );
        tmp.stats = paymentDates;
      } else {
        if (id === "unique") {
          let dataById = stats.uniqueDetails;
          dataById = agregDataByDate(dataById);
          tmp.stats = dataById;
        } else {
          let dataById = stats.data.filter((item) => item.type === id);
          dataById = agregDataByDate(dataById);
          tmp.stats = dataById;
        }
      }
      tmp.title = t(
        `myDesign.stats.${
          id === "payments-new"
            ? "payments"
            : id === "unique"
            ? "uniqueVisitor"
            : id
        }Title`
      );
      setStatToGraph(tmp);
      setDetailMode(id);
    };

    const lottieIcons = useMemo(
      () => ({
        payment: (
          <Root>
            <Lottie
              animationData={PaymentLottie}
              className={classes.iconStyle}
            />
          </Root>
        ),
        payments: (
          <Root>
            <Lottie
              animationData={PaymentLottie}
              className={classes.iconStyle}
            />
          </Root>
        ),
        "payments-new": (
          <Root>
            <Lottie
              animationData={PaymentLottie}
              className={classes.iconStyle}
            />
          </Root>
        ),
        webview: (
          <Root>
            <Lottie
              animationData={WebviewLottie}
              className={classes.iconStyle}
            />
          </Root>
        ),
        view: (
          <Root>
            <Lottie animationData={ViewLottie} className={classes.iconStyle} />
          </Root>
        ),
        unique: (
          <Root>
            <Lottie
              animationData={UniqueLottie}
              className={classes.iconStyle}
            />
          </Root>
        ),
        launch: (
          <Root>
            <Lottie
              animationData={RocketLaunch}
              className={classes.iconStyle}
            />
          </Root>
        ),
        like: (
          <Root>
            <Lottie animationData={LikeLottie} className={classes.iconStyle} />
          </Root>
        ),
        share: (
          <Root>
            <Lottie animationData={ShareLottie} className={classes.iconStyle} />
          </Root>
        ),
        paces: (
          <Root>
            <Lottie animationData={PacesLottie} className={classes.iconStyle} />
          </Root>
        ),
      }),
      []
    );

    return (
      <Root style={{}}>
        <Card
          className={classes.card}
          sx={{
            margin: props.fromUserDashboard
              ? "0 1rem 1rem 0.5rem"
              : "0 1rem 1rem 0",
          }}
          onClick={() => (disableClick ? undefined : handleStatsForGraph(id))}
          style={{
            cursor: !disableClick ? "pointer" : "auto",
          }}
        >
          {width >= 960 ? (
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={width >= 960 ? "25px" : "0"}
              >
                <Box>{lottieIcons[id]}</Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                >
                  <Typography variant="h3" style={{ fontSize: "35px" }}>
                    {value}
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      marginBottom: !disableClick ? "5px" : "10px",
                      textAlign: "center",
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  style={{
                    fontSize: "14px",
                    fontStyle: "italic",
                    lineHeight: "1.2",
                    textAlign: "left",
                  }}
                >
                  {caption}
                </Typography>
              </Box>
              {!disableClick && (
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.seeMoreButton}
                  onClick={() => {
                    setFilterIdValue(id);
                    handleStatsForGraph(id);
                  }}
                >
                  <span className={classes.seeMoreButtonText}>
                    {t("myExpariences.detailsButton")}
                  </span>
                </Button>
              )}
            </CardContent>
          ) : (
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={!disableClick ? "2px" : "6px"}
              >
                <Box>{lottieIcons[id]}</Box>
                <Typography variant="h3" style={{ fontSize: "30px" }}>
                  {value}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    marginBottom: !disableClick ? "5px" : "10px",
                    textAlign: "center",
                  }}
                >
                  {title}
                </Typography>
              </Box>
              <Box sx={{ lineHeight: "0.8", textAlign: "center" }}>
                <Typography
                  variant="caption"
                  style={{
                    fontSize: "11px",
                    fontStyle: "italic",
                  }}
                >
                  {caption}
                </Typography>
              </Box>
              {!disableClick && (
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.seeMoreButton}
                  onClick={() => {
                    setFilterIdValue(id);
                    handleStatsForGraph(id);
                  }}
                >
                  <span className={classes.seeMoreButtonText}>
                    {t("myExpariences.detailsButton")}
                  </span>
                </Button>
              )}
            </CardContent>
          )}
        </Card>
      </Root>
    );
  };

  if (!stats || reloading) {
    return (
      <Root>
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Root>
    );
  } else {
    return (
      <Root
        sx={{
          margin: "auto 0",
          position: "relative",
          width: "100%",
          marginBottom: props.fromUserDashboard ? "5rem" : 0,
        }}
      >
        {classesTab.open && (
          <Button
            variant="contained"
            color="primary"
            size={width >= 768 ? "medium" : "small"}
            onClick={() => {
              setClassesTab({ ...classesTab, open: !classesTab.open });
            }}
            sx={{
              position: "absolute",
              top: width >= 768 ? "40px" : "15px",
              left: width >= 768 ? "90px" : "9%",
              zIndex: 999,
            }}
          >
            <ArrowBackIcon />
          </Button>
        )}
        <Box
          sx={
            props.fromUserDashboard
              ? {
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 auto",
                  maxWidth: width > 768 ? "650px" : "320px",
                  alignItems: "center",
                  position: "relative",
                }
              : {
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }
          }
        >
          <Box sx={{ position: "absolute", left: 0 }}>
            {props.fromUserDashboard && (
              <GlobeButton
                setGlobe={setGlobe}
                globe={globe}
                width={width}
                t={t}
                fromUserDashboard
              />
            )}
          </Box>
          <Typography
            variant="h5"
            color="#D3AF5F"
            sx={{
              padding: width >= 768 ? "40px 0" : "15px 0",
              marginBottom: width >= 768 ? "0" : "10px",
            }}
          >
            {t("topMenu.statistics")}
          </Typography>
        </Box>
        {props.fromUserDashboard
          ? null
          : !classesTab.open && (
              <GlobeButton
                setGlobe={setGlobe}
                globe={globe}
                width={width}
                t={t}
              />
            )}
        {props.fromUserDashboard
          ? null
          : !classesTab.open && (
              <Button
                variant="contained"
                color="primary"
                size={width >= 768 ? "medium" : "small"}
                onClick={() => {
                  setClassesTab({ ...classesTab, open: !classesTab.open });
                }}
                sx={{
                  position: "absolute",
                  top: width >= 768 ? "40px" : "15px",
                  right: width >= 768 ? "90px" : "9%",
                }}
              >
                {t("myExpariences.classesStatsButton")}
              </Button>
            )}
        {globe && (
          <GlobeGl
            exparienceId={props.exparience._id}
            filterIdValue={filterIdValue}
            dataToConcat={
              filterIdValue === "unique"
                ? stats.uniqueDetails.map((el) => ({
                    ...el,
                    subType: "unique",
                  }))
                : []
            }
          />
        )}
        {!globe && (
          <Container>
            <Fade in={classesTab.open} timeout={500} unmountOnExit>
              <div>
                <ClassStats exparience={props.exparience} />
              </div>
            </Fade>
            <Fade
              in={!detailMode && !classesTab.open}
              timeout={500}
              unmountOnExit
            >
              <div
                className={classes.root}
                style={{
                  justifyContent: "space-evenly",
                  display: detailMode ? "none" : "flex",
                }}
              >
                {!props.fromUserDashboard && (
                  <Accordion
                    className={
                      props.fromUserDashboard
                        ? classes.firstAccordionStyleFromUserDashboard
                        : classes.firstAccordionStyle
                    }
                    sx={{
                      width: "100%",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMore className={classes.textAndIconStyle} />
                      }
                      aria-controls="date-and-location-content"
                      id="date-and-location-header"
                    >
                      <Typography color="primary">
                        {t("myExpariences.stats.dateAndLocationTitle")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className={classes.dateAndLocation}>
                        <FormControl>
                          <FormLabel id="radio-buttons-group-date-select">
                            Dates
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="radio-buttons-group-date-select"
                            defaultValue="ever"
                            className={classes.radioGroupStyle}
                            value={timeframe.mode}
                            onChange={(e) => {
                              setTimeframe({
                                ...timeframe,
                                mode: e.target.value,
                              });
                            }}
                            name="radio-buttons-group"
                            row
                          >
                            <FormControlLabel
                              value="ever"
                              control={<Radio />}
                              label={t("myExpariences.everFilter")}
                            />
                            <FormControlLabel
                              value="month"
                              control={<Radio />}
                              label={t("myExpariences.lastMonthFilter")}
                            />
                            <FormControlLabel
                              value="day"
                              control={<Radio />}
                              label={t("myExpariences.lastWeekFilter")}
                            />
                            <FormControlLabel
                              value="yesterday"
                              control={<Radio />}
                              label={t("myExpariences.yesterdayFilter")}
                            />
                            <FormControlLabel
                              value="custom"
                              control={<Radio />}
                              label={t("myExpariences.customFilter")}
                            />
                          </RadioGroup>
                        </FormControl>
                        {timeframe.mode === "custom" && (
                          <div className={classes.customDatePickers}>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={i18next.language.substring(0, 2)}
                            >
                              <DatePicker
                                disableFuture
                                label={t("myExpariences.stats.startDate")}
                                onChange={(e) =>
                                  setTimeframe({
                                    ...timeframe,
                                    startDate: dayjs(e).format("YYYY-MM-DD"),
                                  })
                                }
                                value={
                                  timeframe.startDate
                                    ? dayjs(timeframe.startDate)
                                    : null
                                }
                              />
                            </LocalizationProvider>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={i18next.language.substring(0, 2)}
                            >
                              <DatePicker
                                disableFuture
                                label={t("myExpariences.stats.endDate")}
                                onChange={(e) =>
                                  setTimeframe({
                                    ...timeframe,
                                    endDate: dayjs(e).format("YYYY-MM-DD"),
                                  })
                                }
                                value={
                                  timeframe.endDate
                                    ? dayjs(timeframe.endDate)
                                    : null
                                }
                              />
                            </LocalizationProvider>
                          </div>
                        )}
                        <FormControl className={classes.selectLocationStyle}>
                          <InputLabel id="select-location-label">
                            Location
                          </InputLabel>
                          <Select
                            labelId="select-location-label"
                            id="demo-simple-select"
                            multiple
                            value={location}
                            label="Location"
                            onChange={(e) => {
                              setLocation(e.target.value);
                            }}
                            input={
                              <OutlinedInput
                                id="select-location-chip"
                                label="Location"
                              />
                            }
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {selected.map((value) => (
                                  <Chip
                                    key={value._id}
                                    label={value.position.title}
                                    deleteIcon={
                                      <Cancel
                                        onMouseDown={(e) => e.stopPropagation()}
                                      />
                                    }
                                    onDelete={() => {
                                      const index = location.findIndex(
                                        (element) => element._id === value._id
                                      );
                                      const tmp = [...location];
                                      tmp.splice(index, 1);
                                      setLocation(tmp);
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                            MenuProps={MenuProps}
                          >
                            {props.exparience.positions.map((item) => {
                              return (
                                <MenuItem
                                  key={`select-location-key-${item._id}`}
                                  value={item}
                                >
                                  {item.position.title}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className={classes.defaultDiv}
                      >
                        <div className={classes.locationsContainer}>
                          <div className={classes.mainButtonsContainer}>
                            <Button
                              startIcon={<Add />}
                              variant="contained"
                              color="primary"
                              // size="small"
                              className={classes.addLocationButton}
                              disabled={handleEmptyCustom()}
                              onClick={() => {
                                const tmp = [...customLocation];
                                tmp.push({
                                  options: [],
                                  coordinates: null,
                                  place_id: null,
                                  label: null,
                                  inputValue: null,
                                  radius: "100",
                                  loading: false,
                                  uniqueKey: guidGenerator(),
                                });
                                setCustomLocation(tmp);
                              }}
                            >
                              {t("myExpariences.stats.addLocation")}
                            </Button>
                            <Button
                              startIcon={<Sort />}
                              variant="contained"
                              color="primary"
                              // size="small"
                              className={classes.sortLocationButton}
                              onClick={() => handleFilters()}
                            >
                              {t("myExpariences.stats.filter")}
                            </Button>
                            <Button
                              startIcon={<FilterListOff />}
                              variant="contained"
                              color="primary"
                              // size="small"
                              className={classes.sortLocationButton}
                              onClick={() => {
                                setLocation([]);
                                setCustomLocation([]);
                                setStats(unfilteredStats);
                              }}
                            >
                              {t("myExpariences.stats.resetFilters")}
                            </Button>
                          </div>
                          <div className={classes.optionsFormat}>
                            {customLocation.map((item, index) => {
                              return (
                                <Paper
                                  key={`location-paper-${item.uniqueKey}`}
                                  elevation={20}
                                  className={classes.frameOptionsContainer}
                                >
                                  <Autocomplete
                                    label={t("configurator.guests")}
                                    id="autocomplete-location"
                                    disableClearable
                                    freeSolo={true}
                                    className={classes.autocomplete}
                                    options={item.options}
                                    isOptionEqualToValue={(option, value) =>
                                      option.label === value.label
                                    }
                                    loading={item.loading}
                                    getOptionLabel={(option) => {
                                      return `${option.label}`;
                                    }}
                                    renderOption={(props, option) => {
                                      return (
                                        <li {...props} key={option.place_id}>
                                          {option.label}
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => {
                                      return (
                                        <TextField
                                          {...params}
                                          placeholder="Location"
                                          label={t(
                                            "myExpariences.stats.customLocation"
                                          )}
                                          variant="outlined"
                                          onChange={(e) => {
                                            const tmp = [...customLocation];
                                            tmp[index].inputValue =
                                              e.target.value;
                                            setCustomLocation(tmp);
                                            promiseOptions(
                                              e.target.value,
                                              index
                                            );
                                          }}
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <React.Fragment>
                                                {item.loading ? (
                                                  <CircularProgress
                                                    color="inherit"
                                                    size={20}
                                                  />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                              </React.Fragment>
                                            ),
                                          }}
                                          value={item.inputValue}
                                        />
                                      );
                                    }}
                                    value={null}
                                    onChange={(e, value) => {
                                      const tmp = [...customLocation];
                                      tmp[index] = {
                                        ...tmp[index],
                                        coordinates: value.coordinates,
                                        label: value.label,
                                        place_id: value.place_id,
                                        options: [],
                                      };
                                      setCustomLocation(tmp);
                                    }}
                                  />
                                  <TextField
                                    key={`location-radius-${index}`}
                                    className={classes.frameTotalStyleQuantity}
                                    type="number"
                                    InputProps={{ inputProps: { min: 0 } }}
                                    variant="outlined"
                                    label={t("myExpariences.stats.radius")}
                                    value={item.radius ? item.radius : ""}
                                    error={!item.radius}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "e" ||
                                        e.key === "E" ||
                                        e.key === "-" ||
                                        e.key === "+"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={(e) => {
                                      const tmp = [...customLocation];
                                      tmp[index] = {
                                        ...tmp[index],
                                        radius: e.target.value,
                                      };
                                      setCustomLocation(tmp);
                                    }}
                                  />
                                  <IconButton
                                    color="primary"
                                    className={classes.deleteIconStyle}
                                    onClick={() => {
                                      const tmp = [...customLocation];
                                      tmp.splice(index, 1);
                                      setCustomLocation(tmp);
                                    }}
                                    size="large"
                                  >
                                    <Clear />
                                  </IconButton>
                                </Paper>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                )}

                {(stats["payments-new"] ||
                  props.exparience.offeringId?.length > 0) && (
                  <CustomCard
                    title={t("myExpariences.bought")}
                    caption={t("myExpariences.caption.bought")}
                    value={stats.payment || stats["payments-new"].data.length}
                    // icon={<MonetizationOnIcon className={classes.iconStyle} />}
                    // icon={<Lottie animationData={PaymentLottie} className={classes.iconStyle} />}
                    id="payments-new"
                    disableClick={stats["payments-new"].data.length === 0}
                  />
                )}
                <CustomCard
                  title={t("myExpariences.webview")}
                  caption={t("myExpariences.caption.webview")}
                  value={stats.webview}
                  // icon={<LanguageIcon className={classes.iconStyle} />}
                  // icon={<Lottie animationData={WebviewLottie} className={classes.iconStyle} />}
                  id="webview"
                  disableClick={stats.webview === 0}
                />
                <CustomCard
                  title={t("myExpariences.viewed")}
                  caption={t("myExpariences.caption.viewed")}
                  value={stats.view}
                  // icon={<VisibilityIcon className={classes.iconStyle} />}
                  // icon={<Lottie animationData={ViewLottie} className={classes.iconStyle} />}
                  id="view"
                  disableClick={stats.view === 0}
                />
                <CustomCard
                  title={t("myExpariences.uniqueVisitor")}
                  caption={t("myExpariences.caption.uniqueVisitor")}
                  value={stats.unique}
                  // icon={<PersonIcon className={classes.iconStyle} />}
                  // icon={<Lottie animationData={UniqueLottie} className={classes.iconStyle} />}
                  id="unique"
                  disableClick={stats.unique === 0}
                />
                <CustomCard
                  title={t("myExpariences.launched")}
                  caption={t("myExpariences.caption.launched")}
                  value={stats.launch}
                  // icon={<Lottie animationData={RocketLaunch} className={classes.iconStyle} />}
                  // icon={<PlayCircleOutlineIcon className={classes.iconStyle} />}
                  id="launch"
                  disableClick={stats.launch === 0}
                />
                <CustomCard
                  title={t("myExpariences.liked")}
                  caption={t("myExpariences.caption.liked")}
                  value={stats.like}
                  // icon={<FavoriteIcon className={classes.iconStyle} />}
                  // icon={<Lottie animationData={LikeLottie} className={classes.iconStyle} />}
                  id="like"
                  disableClick={stats.like === 0}
                />
                <CustomCard
                  title={t("myExpariences.shared")}
                  caption={t("myExpariences.caption.shared")}
                  value={stats.share}
                  // icon={<ShareIcon className={classes.iconStyle} />}
                  // icon={<Lottie animationData={ShareLottie} className={classes.iconStyle} />}
                  id="share"
                  disableClick={stats.share === 0}
                />
                <CustomCard
                  title={t("myExpariences.paces")}
                  caption={t("myExpariences.caption.paces")}
                  value={stats.paces}
                  // icon={<img src={FootprintIcon} className={classes.iconStyle} />}
                  // icon={<Lottie animationData={PacesLottie} className={classes.iconStyle} />}
                  id="paces"
                  disableClick
                />
              </div>
            </Fade>
            <Fade
              in={detailMode && !classesTab.open ? true : false}
              timeout={1000}
              unmountOnExit
            >
              <div
                className={classes.root}
                style={{
                  display: !detailMode ? "none" : "flex",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    setStatToGraph(null);
                    setDetailMode(null);
                    setFilterIdValue(null);
                  }}
                >
                  {t("myDesign.stats.goBackButton")}
                </Button>
                <LineChart
                  statToGraph={statToGraph}
                  title={statToGraph ? statToGraph.stats.title : ""}
                  responsive={width < 767}
                />
              </div>
            </Fade>
          </Container>
        )}
      </Root>
      // </div>
    );
  }
};

export default MyExparienceStats;

const GlobeButton = ({
  setGlobe,
  globe,
  width,
  t,
  fromUserDashboard = false,
}) => {
  return (
    <Button
      variant="contained"
      id="zob"
      color="primary"
      size={width >= 768 ? "medium" : "small"}
      onClick={() => {
        setGlobe(!globe);
      }}
      sx={
        fromUserDashboard
          ? { height: "40px" }
          : {
              position: "absolute",
              top: width >= 768 ? "40px" : "15px",
              left: width >= 768 ? "90px" : "9%",
            }
      }
    >
      {!globe ? t("myExpariences.globeButton") : t("myExpariences.cardsButton")}
    </Button>
  );
};
